import {useEffect, useState} from 'react'
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'

import {useDisclourse} from '../../hooks/useDisclourse'
import {useLazyQuery, useMutation} from '@apollo/client'
import {GET_ALL_SIGNATURE_FORMS_API, GET_ONE_SIGNATURE_FORM_API} from '../../gql/queries/signatureFormsApiQueries'
import {toast} from 'react-toastify'
import {
  IResSignatureFormsAPI,
  IResCreateSignatureForm,
  NodeSignatureForm,
} from '../../types/signatureFormsApi'
import {parseId, hasPermission} from '../../helpers'
import moment from 'moment'
import {ModalPreview} from './components/ModalPreview'
import {SIZE_PAGE_DEFAULT, ALL_PERMISSIONS} from '../../helpers/consts'
import {PaginationUtils} from '../shared/PaginationUtils'
import {UPDATE_SIGNATURE_FORM, UPDATE_SIGNATURE_FORM_STATUS} from '../../gql/mutations/signatureFormsMutations'
import {PaginationSection} from '../shared/PaginationSection'
import {ModalAddSignatureForm} from './components/ModalAddSignatureForm'
import {BadgeStatus} from './components/BadgeStatus'
import {node} from 'webpack'
import {useGetUser} from '../../store/userStore'

export const SignatureFormsPage = () => {

  const user = useGetUser((s) => s.user)
  const permissions = useGetUser((s) => s.user?.permissions)
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const canEditSignatureForm = permissions && hasPermission(permissions, ALL_PERMISSIONS.EDIT_SIGNATURE_FORMS)
  const canAddSignatureForm = permissions && hasPermission(permissions, ALL_PERMISSIONS.ADD_SIGNATURE_FORMS)

  // Check if user is Agente, Asesor, or Supervisor
  const isRestrictedRole = userGroups.includes('Agente') || userGroups.includes('Asesor') || userGroups.includes('Supervisor')
  

  const [gqlGetSignatureForms, {data: dataAllSignatureForms, loading, error, refetch}] =
    useLazyQuery<IResSignatureFormsAPI>(GET_ALL_SIGNATURE_FORMS_API, {
      fetchPolicy: 'no-cache',
    })

  // Lazy query for fetching one signature form by ID
  const [getSignatureFormById, {data: dataSignatureForm}] = useLazyQuery(GET_ONE_SIGNATURE_FORM_API, {fetchPolicy: 'no-cache'})

  const [gqlCreateP, {data: dataUpdate, loading: loadingUpdate, error: errorUpdate}] =
    useMutation<IResCreateSignatureForm>(UPDATE_SIGNATURE_FORM)

  const [updateSignatureFormStatus] = useMutation(UPDATE_SIGNATURE_FORM_STATUS)

  const {isOpen, onClose, onOpen} = useDisclourse()
  const {isOpen: isOpenPreview, onClose: onClosePreview, onOpen: onOpenPreview} = useDisclourse()

  const [infoPreview, setInfoPreview] = useState<null | NodeSignatureForm>(null)
  // 'new' | 'edit' | 'duplicate' | null
  const [mode, setMode] = useState<null | 'new' | 'edit' | 'duplicate'>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)

  const [isPag, setIsPag] = useState(false)
  const [page, setPage] = useState(1)

  const [dataArr, setDataArr] = useState<IResSignatureFormsAPI['allSignatureForms']['edges']>([])

  let totalSignatureForms = dataAllSignatureForms?.allSignatureForms?.totalCount ?? 0

  const totalPages = Math.ceil(totalSignatureForms / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, totalSignatureForms)

  const navigate = useNavigate()


  const initialFormData = {
    name: '',
    description: '',
    formContent: '',
    emailContent: '',
    smsContent: '',
    status: '', // 'ACTIVE' | 'INACTIVE'
    user: '', // ID del usuario creador
  }

  const [formData, setFormData] = useState(initialFormData)

  const handleFilters = () => {
    const variables = {
      first: first,
      offset: (currentPage - 1) * first,
      name: formData.name || null,
      status: filterStatus === 'all' ? null : filterStatus,
    }
    gqlGetSignatureForms({
      variables,
    })
  }

  type IFilterStatus = 'all' | 'ACTIVE' | 'INACTIVE'
  const [filterStatus, setFilterStatus] = useState<IFilterStatus>('ACTIVE')

  useEffect(() => {
    handleFilters()
    setSignatureFormsSelected([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, gqlGetSignatureForms, currentPage])

  const [signatureFormsSelected, setSignatureFormsSelected] = useState<
    {id: string; name: string; status: string; user: string}[] | undefined
  >([])

  const isAllSelected =
    signatureFormsSelected?.length === dataAllSignatureForms?.allSignatureForms?.edges?.length

  const toggleAll = () => {
    if (isAllSelected) {
      setSignatureFormsSelected([])
    } else {
      if (dataAllSignatureForms?.allSignatureForms?.edges?.length) {
        const allIds = dataAllSignatureForms.allSignatureForms.edges.map((edge) => ({
          id: edge.node.id,
          name: edge.node.name,
          status: edge.node.status,
          user: edge.node.user
            ? edge.node.user?.firstName ?? ' ' + edge.node.user?.lastName ?? ''
            : '',
        }))
        setSignatureFormsSelected(allIds)
      }
    }
  }

  const handlePolicyName = () => {
    handleFilters()
  }

  useEffect(() => {
    if (!dataAllSignatureForms) return
    const {edges} = dataAllSignatureForms.allSignatureForms
    // if (edges.length === 0) return
    if (isPag) {
      setDataArr((p) => [...p, ...edges])
    } else {
      setDataArr(edges)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAllSignatureForms])

  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error.message}`)
    }
  }, [error])

  const updatePage = (n: number) => {
    setCurrentPage(n)
  }

  const handleUpdatePolicy = async (id: string, data: any) => {
    gqlCreateP({
      variables: {
        id: parseId(id),
        input: data,
      },
    })
  }
  function removeKeysDeep(obj: any, keysToRemove: string[] = ['__typename']): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => removeKeysDeep(item, keysToRemove))
    } else if (obj && typeof obj === 'object') {
      const newObj: Record<string, any> = {}
      for (const key in obj) {
        if (!keysToRemove.includes(key)) {
          newObj[key] = removeKeysDeep(obj[key], keysToRemove)
        }
      }
      return newObj
    }
    return obj
  }

  useEffect(() => {
    if (dataUpdate) {
      toast.success('Signature Form updated successfully')
      refetch()
    }
  }, [dataUpdate])
  // if ((loading || !dataAllSignatureForms) && !isPag) {
  //   return <LoadingSignatureForms />
  // }

  return (
    <div className='signatureForms-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='fs-3'>Signature Forms List</Breadcrumb.Item>
        </Breadcrumb>
        <div className='d-flex align-items-center gap-2'>
          {canAddSignatureForm && (
            <Button
              size='sm'
              onClick={() => {
                setMode('new')
                setInfoPreview(null)
                onOpen()
              }}
            >
              <i className='bi bi-plus fs-3' /> New Signature Form
            </Button>
          )}
        </div>
      </header>
      <Row className='signatureForms'>
        <Col className='container'>
          <div className='signatureForms-container'>
            <div className='title-header'>
              <h3>All SignatureForms</h3>
            </div>
            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  disabled={false}
                  placeholder='Type your query here...'
                  aria-label="Recipient's username with two button addons"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault() // evita el submit automático
                      handlePolicyName() // si tienes una función como esa
                    }
                  }}
                  value={formData.name}
                />
                <Form.Select
                  value={filterStatus}
                  aria-label='Filter by status'
                  onChange={(e) => setFilterStatus(e.target.value as IFilterStatus)}
                >
                  <option value='all'>Filter by Status</option>
                  <option value='ACTIVE'>Active</option>
                  <option value='INACTIVE'>Inactive</option>
                </Form.Select>
                <Button
                  disabled={false}
                  onClick={() => handleFilters()}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  Search
                </Button>
                <Button
                  disabled={false}
                  variant='outline-primary'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  Filter
                </Button>
              </InputGroup>
            </div>
            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {
                  setFirst(n)
                  setPage(1)
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalSignatureForms}
                updatePage={updatePage}
                PaginationUtils={PaginationUtils}
              />
              <div className='col-md-3'>
                <div className='results-filter'>
                  <Col lg={12} md={12} sm={12} xs={12}></Col>
                </div>
              </div>
            </div>

            <div className='table-responsive table table-striped table-hover '>
              <table
                className='table-scrollable table-scroll  table table-striped table-hover '
                style={{whiteSpace: 'nowrap'}}
              >
                <thead>
                  <tr role='row'>
                    <th></th>
                    <th className='fw-bold'>Name</th>
                    <th className='fw-bold'>Status</th>
                    <th className='fw-bold'>Created</th>
                    <th className='fw-bold'>Created by</th>
                    <th className='fw-bold'>Signed / Requests</th>
                    <th className='fw-bold'>Actions</th>
                  </tr>
                </thead>
                {(loading || !dataAllSignatureForms) && !isPag ? (
                  <tbody>
                    <tr>
                      <td colSpan={8}>
                        <div className='d-flex flex-center' style={{height: '30vh'}}>
                          <Spinner animation='border' />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {dataArr.length === 0 && (
                      <tr>
                        <td colSpan={8}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            There are no signatureForms currently added
                          </div>
                        </td>
                      </tr>
                    )}
                    {loading && (
                      <tr>
                        <td colSpan={8}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            <Spinner animation='border' />
                          </div>
                        </td>
                      </tr>
                    )}
                    {dataArr.slice(first * page - first, first * page).map(({node: nodeSF}) => (
                      <tr key={nodeSF.id}>
                        <td>
                          <div className='set-quick-status'>
                            <div></div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <span
                              className='text-primary'
                              style={{cursor: 'pointer'}}
                              data-tooltip-id={`myt2${parseId(nodeSF.id)}`}
                            >
                              {nodeSF.name}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div>
                            <BadgeStatus state={nodeSF.status} />
                          </div>
                        </td>
                        {/* <td>
                        
                      </td> */}
                        <td>{moment(nodeSF.created).format('DD MMM, YY')}</td>
                        <td>
                          {nodeSF.user?.firstName} {nodeSF.user?.lastName}
                        </td>
                        <td>
                          <div style={{display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '200px'}}>
                            <div style={{fontSize: '13px', color: '#5e6278'}}>
                              <span style={{color: '#50cd89', marginRight: '4px'}}>●</span>
                              Signed ({nodeSF.signedEntriesCount ?? 0}) /
                              <span style={{color: '#ffc700', marginLeft: '4px'}}>●</span>
                              <span style={{marginLeft: '4px'}}>Requests ({nodeSF.entriesCount ?? 0})</span>
                            </div>
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <div style={{
                                flex: 1,
                                height: '24px',
                                backgroundColor: '#ffc700',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                position: 'relative'
                              }}>
                                <div style={{
                                  height: '100%',
                                  backgroundColor: '#50cd89',
                                  width: `${nodeSF.entriesCount ? Math.round((nodeSF.signedEntriesCount ?? 0) / nodeSF.entriesCount * 100) : 0}%`,
                                  transition: 'width 0.3s ease'
                                }}></div>
                                <span style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  color: '#fff',
                                  textShadow: '0 0 2px rgba(0,0,0,0.5)'
                                }}>
                                  {nodeSF.entriesCount ? Math.round((nodeSF.signedEntriesCount ?? 0) / nodeSF.entriesCount * 100) : 0}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Dropdown as={ButtonGroup}>
                            <Button
                              variant='outline-primary'
                              size='sm'
                              style={{border: '1px solid #3e97ff'}}
                              onClick={() => {
                                getSignatureFormById({variables: {id: parseId(nodeSF.id)}})
                                onOpenPreview()
                              }}
                            >
                              Preview
                            </Button>
                            <Dropdown.Toggle
                              split
                              variant='outline-primary'
                              size='sm'
                              id='dropdown-custom-2'
                              style={{border: '1px solid #3e97ff'}}
                            />
                            <Dropdown.Menu className='dropdown-menu-signatureForm'>
                              {(nodeSF.entriesCount ?? 0) > 0 && (
                                <Dropdown.Item
                                  eventKey='entries'
                                  onClick={() => navigate(`/signature-forms/${nodeSF.id}/entries`)}
                                >
                                  <i className='bi bi-arrow-repeat' /> View Entries
                                </Dropdown.Item>
                              )}
                              {canEditSignatureForm && (
                              <>
                                {nodeSF.status === 'INACTIVE' && !isRestrictedRole && (
                                  <Dropdown.Item
                                    eventKey='activate'
                                    onClick={async () => {
                                      try {
                                        const {data} = await updateSignatureFormStatus({
                                          variables: {id: parseId(nodeSF.id), status: 'ACTIVE'},
                                        })
                                        if (data?.signatureFormUpdateStatus?.success) {
                                          toast.success('Signature Form marked as active')
                                          refetch()
                                        } else {
                                          toast.error(
                                            data?.signatureFormUpdateStatus?.errors?.[0]?.message || 'Error updating status'
                                          )
                                        }
                                      } catch (e: any) {
                                        toast.error(e.message)
                                      }
                                    }}
                                  >
                                    <i className='bi bi-check-square-fill' /> Mark as active
                                  </Dropdown.Item>
                                )}

                                {nodeSF.status === 'ACTIVE' && !isRestrictedRole && (
                                  <Dropdown.Item
                                    eventKey='deactivate'
                                    onClick={async () => {
                                      try {
                                        const {data} = await updateSignatureFormStatus({
                                          variables: {id: parseId(nodeSF.id), status: 'INACTIVE'},
                                        })
                                        if (data?.signatureFormUpdateStatus?.success) {
                                          toast.success('Signature Form marked as inactive')
                                          refetch()
                                        } else {
                                          toast.error(
                                            data?.signatureFormUpdateStatus?.errors?.[0]?.message || 'Error updating status'
                                          )
                                        }
                                      } catch (e: any) {
                                        toast.error(e.message)
                                      }
                                    }}
                                  >
                                    <i className='bi bi-folder-fill' /> Mark as inactive
                                  </Dropdown.Item>
                                )}
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  eventKey='edit'
                                  onClick={async (e) => {
                                    e.preventDefault()
                                    setMode('edit')
                                    await getSignatureFormById({variables: {id: parseId(nodeSF.id)}})
                                    setInfoPreview(null)
                                    onOpen()
                                  }}
                                >
                                  <i className='bi bi-pencil-fill' /> Edit
                                </Dropdown.Item>
                                {!isRestrictedRole && (
                                  <Dropdown.Item
                                    eventKey='duplicate'
                                    onClick={async (e) => {
                                      e.preventDefault()
                                      setMode('duplicate')
                                      const result = await getSignatureFormById({variables: {id: parseId(nodeSF.id)}})
                                      const form = result?.data?.signatureFormById
                                      if (form) {
                                        setInfoPreview({
                                          ...form,
                                          id: '', // aseguramos que sea tratado como nuevo
                                          name: `${form.name} Copy`,
                                        })
                                        // Ensure dataSignatureForm is not used in duplicate mode
                                      }
                                      onOpen()
                                    }}
                                  >
                                    <i className='bi bi-files' /> Duplicate form
                                  </Dropdown.Item>
                                )}
                              </>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && (
        <ModalAddSignatureForm
          refetch={refetch}
          isOpen={isOpen}
          onClose={onClose}
          keyboard={false}
          backdrop='static'
          idFormEdit={
            mode === 'duplicate'
              ? infoPreview
              : mode === 'edit'
              ? dataSignatureForm?.signatureFormById
              : null
          }
        />
      )}
      {isOpenPreview && dataSignatureForm?.signatureFormById && (
        <ModalPreview
          info={dataSignatureForm.signatureFormById}
          isOpen={isOpenPreview}
          onClose={onClosePreview}
        />
      )}
    </div>
  )
}
